import { useEffect, useState, useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'
import { lumi } from '@/lib/lumi'
import Header from '@/components/Header'
import Footer from '@/components/Footer'
import NewsCard from '@/components/NewsCard'
import BannerDisplay from '@/components/BannerDisplay'
import type { NewsArticle, Banner, SiteSetting } from '@/types'
import {Loader2, Search, X, ChevronLeft, ChevronRight} from 'lucide-react'
import { trackPageView } from '@/lib/trackPageView'

const PAGE_SIZE = 50

export default function Home() {
  const [allArticles, setAllArticles] = useState<NewsArticle[]>([])
  const [banners, setBanners] = useState<Banner[]>([])
  const [loading, setLoading] = useState(true)
  const [searchParams, setSearchParams] = useSearchParams()
  const [searchQuery, setSearchQuery] = useState(searchParams.get('busca') || '')

  const currentPage = Math.max(1, parseInt(searchParams.get('pagina') || '1', 10))

  const fetchData = useCallback(async () => {
    setLoading(true)
    try {
      const query = searchParams.get('busca') || ''
      const filter: Record<string, unknown> = { status: 'published' }

      const [articlesRes, bannersRes, settingsRes] = await Promise.all([
        lumi.entities.news_articles.list({
          filter,
          sort: { createdAt: -1 },
          limit: 500,
        }),
        lumi.entities.banners.list({
          filter: { isActive: true },
          sort: { order: 1 },
          limit: 30,
        }),
        lumi.entities.site_settings.list({
          filter: { key: { $in: ['site_title', 'meta_description'] } },
          limit: 5,
        }),
      ])

      let articleList = articlesRes.list as NewsArticle[]
      if (query) {
        const q = query.toLowerCase()
        articleList = articleList.filter(a =>
          a.title.toLowerCase().includes(q) ||
          a.content.toLowerCase().includes(q) ||
          a.authorName.toLowerCase().includes(q) ||
          (a.category || '').toLowerCase().includes(q)
        )
        setSearchQuery(query)
      }

      const settings = settingsRes.list as SiteSetting[]
      const titleSetting = settings.find(s => s.key === 'site_title')
      const descSetting  = settings.find(s => s.key === 'meta_description')
      document.title = titleSetting?.value || 'CARLOS FELIPE'
      const metaDesc = document.querySelector('meta[name="description"]')
      if (metaDesc) metaDesc.setAttribute('content', descSetting?.value || 'Fique por dentro das últimas notícias')

      setAllArticles(articleList)
      setBanners(bannersRes.list as Banner[])
    } catch (e) {
      console.error('Erro ao carregar notícias:', e)
    } finally {
      setLoading(false)
    }
  }, [searchParams])

  useEffect(() => { fetchData() }, [fetchData])

  useEffect(() => {
    trackPageView('home')
  }, [])

  const handleSearch = (query: string) => {
    if (query) setSearchParams({ busca: query })
    else setSearchParams({})
  }

  const clearSearch = () => {
    setSearchQuery('')
    setSearchParams({})
  }

  const totalPages = Math.ceil(allArticles.length / PAGE_SIZE)
  const safePage = Math.min(currentPage, Math.max(1, totalPages))
  const articles = allArticles.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE)
  const interBanners = banners.filter(b => b.position === 'between_articles')

  const goToPage = (page: number) => {
    const params: Record<string, string> = {}
    const q = searchParams.get('busca')
    if (q) params.busca = q
    if (page > 1) params.pagina = String(page)
    setSearchParams(params)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 transition-colors">
      <Header onSearch={handleSearch} />

      {/* Banner do topo */}
      <BannerDisplay banners={banners} position="header" className="mt-3" />

      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-8">

        {/* Indicador de pesquisa ativo */}
        {searchQuery && (
          <div className="mb-6 animate-fade-in">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm text-sm">
              <Search size={13} className="text-red-600 shrink-0" />
              <span className="text-gray-500 dark:text-gray-400">
                Resultados para: <strong className="text-gray-800 dark:text-gray-200">{searchQuery}</strong>
              </span>
              <button
                onClick={clearSearch}
                className="ml-1 w-5 h-5 flex items-center justify-center rounded-full bg-gray-100 dark:bg-gray-800 text-gray-400 hover:bg-red-50 dark:hover:bg-red-950/40 hover:text-red-600 transition-all"
                aria-label="Limpar pesquisa"
              >
                <X size={11} />
              </button>
            </div>
          </div>
        )}

        {/* Cabeçalho da secção de notícias */}
        {!searchQuery && !loading && allArticles.length > 0 && (
          <div className="flex items-center gap-3 mb-7">
            <div className="w-1 h-6 bg-red-600 rounded-full" />
            <h1 className="font-serif text-xl font-bold text-gray-900 dark:text-white tracking-tight">
              Últimas Notícias
            </h1>
            <div className="flex-1 h-px bg-gray-200 dark:bg-gray-800" />
            {totalPages > 1 && (
              <span className="text-xs text-gray-400 dark:text-gray-500 font-medium shrink-0">
                Página {safePage} de {totalPages}
              </span>
            )}
          </div>
        )}

        {loading ? (
          <div className="flex flex-col items-center justify-center py-32 gap-4">
            <Loader2 size={36} className="text-red-600 animate-spin" />
            <p className="text-sm text-gray-400 dark:text-gray-500 font-medium">A carregar notícias...</p>
          </div>
        ) : allArticles.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-32 gap-4 text-center">
            <div className="w-16 h-16 rounded-2xl bg-gray-100 dark:bg-gray-800 flex items-center justify-center shadow-inner">
              <Search size={24} className="text-gray-300 dark:text-gray-600" />
            </div>
            <div>
              <p className="text-lg font-bold text-gray-700 dark:text-gray-300 mb-1">
                {searchQuery ? 'Nenhuma notícia encontrada' : 'Nenhuma notícia publicada'}
              </p>
              <p className="text-sm text-gray-400 dark:text-gray-500">
                {searchQuery ? 'Tente outros termos de pesquisa.' : 'Aguarde as próximas publicações.'}
              </p>
            </div>
            {searchQuery && (
              <button onClick={clearSearch} className="px-4 py-2 rounded-xl bg-red-600 hover:bg-red-700 text-white text-sm font-semibold transition-colors">
                Ver todas as notícias
              </button>
            )}
          </div>
        ) : (
          <>
            <div className="space-y-10">
              {articles.map((article, index) => (
                <div key={article._id} className="animate-fade-up" style={{ animationDelay: `${Math.min(index * 40, 200)}ms` }}>
                  <NewsCard article={article} />
                  {index < articles.length - 1 && interBanners.length > 0 && (
                    <RotatingBanner banners={interBanners} startIndex={index} />
                  )}
                </div>
              ))}
            </div>

            {/* Paginação */}
            {totalPages > 1 && (
              <Pagination current={safePage} total={totalPages} onPage={goToPage} />
            )}
          </>
        )}
      </main>

      <Footer />
    </div>
  )
}

/* ─────────────────────────────────────────────
   Componente de Paginação
───────────────────────────────────────────── */
function Pagination({ current, total, onPage }: { current: number; total: number; onPage: (p: number) => void }) {
  const getPages = () => {
    if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1)
    const pages: (number | '...')[] = []
    if (current <= 4) {
      pages.push(1, 2, 3, 4, 5, '...', total)
    } else if (current >= total - 3) {
      pages.push(1, '...', total - 4, total - 3, total - 2, total - 1, total)
    } else {
      pages.push(1, '...', current - 1, current, current + 1, '...', total)
    }
    return pages
  }

  const pages = getPages()

  return (
    <nav
      aria-label="Paginação de notícias"
      className="mt-14 mb-4 flex flex-col items-center gap-4"
    >
      {/* Linha divisória tênue */}
      <div className="w-full h-px bg-gray-200 dark:bg-gray-800 mb-2" />

      <div className="flex items-center gap-1.5 flex-wrap justify-center">
        {/* Botão Anterior */}
        <button
          onClick={() => onPage(current - 1)}
          disabled={current === 1}
          aria-label="Página anterior"
          className="
            flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-semibold
            border border-gray-200 dark:border-gray-700
            bg-white dark:bg-gray-900
            text-gray-600 dark:text-gray-400
            hover:bg-gray-50 dark:hover:bg-gray-800
            hover:border-gray-300 dark:hover:border-gray-600
            hover:text-gray-900 dark:hover:text-gray-100
            disabled:opacity-35 disabled:cursor-not-allowed disabled:hover:bg-white dark:disabled:hover:bg-gray-900
            transition-all duration-150 shadow-sm
          "
        >
          <ChevronLeft size={15} />
          <span className="hidden sm:inline">Anterior</span>
        </button>

        {/* Números de página */}
        {pages.map((page, idx) =>
          page === '...' ? (
            <span
              key={`ellipsis-${idx}`}
              className="w-9 h-9 flex items-center justify-center text-gray-400 dark:text-gray-600 text-sm select-none"
            >
              ···
            </span>
          ) : (
            <button
              key={page}
              onClick={() => onPage(page as number)}
              aria-label={`Ir para a página ${page}`}
              aria-current={page === current ? 'page' : undefined}
              className={`
                w-9 h-9 rounded-xl text-sm font-bold transition-all duration-150 shadow-sm
                ${page === current
                  ? 'bg-red-600 text-white border border-red-600 scale-105 shadow-md shadow-red-200 dark:shadow-red-900/40'
                  : 'bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700 hover:bg-red-50 dark:hover:bg-red-950/30 hover:border-red-300 dark:hover:border-red-800 hover:text-red-700 dark:hover:text-red-400'
                }
              `}
            >
              {page}
            </button>
          )
        )}

        {/* Botão Próxima */}
        <button
          onClick={() => onPage(current + 1)}
          disabled={current === total}
          aria-label="Próxima página"
          className="
            flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-semibold
            border border-gray-200 dark:border-gray-700
            bg-white dark:bg-gray-900
            text-gray-600 dark:text-gray-400
            hover:bg-gray-50 dark:hover:bg-gray-800
            hover:border-gray-300 dark:hover:border-gray-600
            hover:text-gray-900 dark:hover:text-gray-100
            disabled:opacity-35 disabled:cursor-not-allowed disabled:hover:bg-white dark:disabled:hover:bg-gray-900
            transition-all duration-150 shadow-sm
          "
        >
          <span className="hidden sm:inline">Próxima</span>
          <ChevronRight size={15} />
        </button>
      </div>

      {/* Resumo textual */}
      <p className="text-xs text-gray-400 dark:text-gray-600 font-medium tracking-wide">
        Página <span className="text-gray-600 dark:text-gray-400 font-bold">{current}</span> de <span className="text-gray-600 dark:text-gray-400 font-bold">{total}</span>
      </p>
    </nav>
  )
}

/* ─────────────────────────────────────────────
   Componente de Banner Rotativo
───────────────────────────────────────────── */
function RotatingBanner({ banners, startIndex }: { banners: Banner[]; startIndex: number }) {
  const [currentIndex, setCurrentIndex] = useState(startIndex % banners.length)

  useEffect(() => {
    if (banners.length <= 1) return
    const interval = setInterval(() => {
      setCurrentIndex(prev => (prev + 1) % banners.length)
    }, 30000)
    return () => clearInterval(interval)
  }, [banners.length])

  const banner = banners[currentIndex]
  if (!banner) return null

  const isVideo = banner.mediaType === 'video' || banner.mediaUrl?.endsWith('.mp4') || banner.mediaUrl?.endsWith('.webm')

  const media = isVideo ? (
    <video src={banner.mediaUrl} autoPlay muted loop playsInline className="w-full h-full object-contain" style={{ pointerEvents: 'none' }} aria-label={banner.title} />
  ) : (
    <img src={banner.mediaUrl} alt={banner.title} className="w-full h-full object-contain" loading="lazy" />
  )

  const bannerBox = (
    <div className="relative overflow-hidden bg-gray-100 dark:bg-gray-800 rounded-xl shadow-sm" style={{ width: '630px', height: '90px', maxWidth: '100%' }}>
      {media}
    </div>
  )

  return (
    <div className="flex justify-center py-5">
      {banner.linkUrl ? (
        <a href={banner.linkUrl} target="_blank" rel="noopener noreferrer" aria-label={banner.title} className="block hover:opacity-90 transition-opacity rounded-xl overflow-hidden">
          {bannerBox}
        </a>
      ) : bannerBox}
    </div>
  )
}
